import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";

import { QUERY_KEYS, useGetTransportAxiosInstance } from "@/constants";
import { TransportInfo } from "@/pages/Plannn/components/CreateUser/CreateCustomPassengerDialog";

export type CreateCustomPassengerType = {
    name: string;
    note: string;
    companions: { name: string; phone?: string | undefined }[];
    arrival?:
        | (Omit<TransportInfo["info"], "addFlightInfo" | "origin" | "destination"> & {
              origin: number | string | null;
              destination: number | string | null;
          })
        | undefined;
    departure?:
        | (Omit<TransportInfo["info"], "addFlightInfo" | "origin" | "destination"> & {
              origin: number | string | null;
              destination: number | string | null;
          })
        | undefined;
};

export const useCreateCustomPassengerMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message: string }, Error, CreateCustomPassengerType>({
        mutationFn: async (payload) => {
            try {
                const res = await axiosInstance.post(`/custom-passengers/`, payload).then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_TASKS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_PASSENGERS],
            });
        },
    });
};

export const useDeleteCustomPassengerMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message: string }, Error, string>({
        mutationFn: async (id) => {
            try {
                const res = await axiosInstance
                    .delete<{ id: string; ok: boolean; message: string }>(`/custom-passengers/${id}`)
                    .then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_TASKS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_PASSENGERS],
            });
        },
    });
};
